import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { certificateService } from '@/services/certificate/certificate.service';
import type { CertificateTemplate } from '@/types/certificate.types';
import { FileText, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CertificateFailedLogs from './certificate-failed-logs';

interface CertificateConfigSectionProps {
  processId: string;
  onTemplateChange?: (template: CertificateTemplate | null) => void;
}

const CertificateConfigSection: React.FC<CertificateConfigSectionProps> = ({
  processId,
  onTemplateChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [processId]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const existingTemplate = await certificateService.getTemplateByProcess(
        processId,
      );
      if (existingTemplate) {
        setTemplate(existingTemplate);
        onTemplateChange?.(existingTemplate);
      }
    } catch (error: any) {
      // Template doesn't exist yet, which is fine
      if (error.response?.status !== 404) {
        console.error('Error loading template:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManageTemplate = () => {
    navigate(`/admin/certificate/template/${processId}`);
  };

  if (loading && !template) {
    return (
      <div className="p-4 border rounded-md bg-gray-50">
        <p className="text-sm text-gray-500">{t('certificate.certificateConfig.loadingCertificateSettings')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-md bg-white space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('certificate.certificateConfig.title')}
          </h3>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleManageTemplate}
        >
          <Settings className="h-4 w-4 mr-2" />
          {template ? t('certificate.certificateConfig.manageTemplate') : t('certificate.certificateConfig.configureCertificate')}
        </Button>
      </div>

      {template ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {template.name}
              </p>
              <p className="text-xs text-gray-500">
                {t('certificate.certificateConfig.templateConfigured')} • {template.fieldMappings?.length || 0} {template.fieldMappings?.length !== 1 ? t('certificate.certificateConfig.fieldsMappedPlural') : t('certificate.certificateConfig.fieldsMapped')} {t('certificate.certificateConfig.mapped')}
              </p>
            </div>
          </div>

          <CertificateFailedLogs processId={processId} />
        </div>
      ) : (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800 mb-3">
            {t('certificate.certificateConfig.noCertificateTemplateConfigured')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CertificateConfigSection;

